import type { CanonicalExam } from '../model/canonical.ts';
import { elapsedMs } from '../exam-engine/clock.ts';
import type { SessionState } from '../exam-engine/state.ts';
import { buildSummary, type ScoredEntry, type SessionSummary } from '../statistics/summary.ts';

export const SESSION_SCHEMA_VERSION = '1.0.0';

/** The entries a summary is built from, in the order the session drew them. */
export function scoredEntries(state: SessionState, at: number): ScoredEntry[] {
  return state.blueprint.questionOrder.map((questionId) => {
    const entry = state.entries[questionId];
    return {
      questionId,
      score: state.scores[questionId] ?? { raw: 0, max: 0, outcome: 'unanswered' },
      durationMs: entry === undefined ? 0 : elapsedMs(entry.segments, at),
      markedForReview: entry?.markedForReview ?? false,
    };
  });
}

export function summarise(exam: CanonicalExam, state: SessionState, at: number): SessionSummary {
  return buildSummary(exam, scoredEntries(state, at), elapsedMs(state.clock.segments, at));
}

/**
 * Serialises a finished session into `session-results.json`.
 *
 * Two things it deliberately does not contain. **No question text**: results reference the exam by
 * hash and identify everything by id, so sharing a result never shares the exam. **Nothing
 * identifying**: no filename, no path, no user agent — the only free text is a comment the
 * candidate typed themselves, and the export screen says so before writing it.
 */
export function toSessionResultsJson(
  exam: CanonicalExam,
  state: SessionState,
  at: number,
  appVersion: string,
): Record<string, unknown> {
  const entries = scoredEntries(state, at);
  const summary = buildSummary(exam, entries, elapsedMs(state.clock.segments, at));

  return {
    $schema: `https://freexamplayer.com/schema/session/${SESSION_SCHEMA_VERSION}/session.schema.json`,
    kind: 'session-results',
    schemaVersion: SESSION_SCHEMA_VERSION,

    session: {
      id: state.id,
      appVersion,
      // Only when the candidate typed one. It is the one field here that identifies a person, it
      // is present because they asked for it, and it is absent by default.
      ...(state.candidateName !== '' && { candidateName: state.candidateName }),
      modePreset: state.modePreset,
      startedAt: state.startedAt,
      ...(state.submittedAt !== null && { submittedAt: state.submittedAt }),
      status: state.status === 'completed' || state.status === 'reviewing' ? 'submitted' : 'abandoned',

      exam: {
        examId: state.examRef.examId,
        schemaVersion: state.examRef.schemaVersion,
        contentVersion: state.examRef.contentVersion,
        contentHash: state.examRef.contentHash,
        title: state.examRef.title,
        questionCount: state.examRef.questionCount,
      },

      criteria: {
        count: state.criteria.count,
        ...(state.criteria.range !== null && { range: { ...state.criteria.range } }),
        ...(state.criteria.incorrectAtLeast !== null && {
          incorrectAtLeast: state.criteria.incorrectAtLeast,
        }),
        categoryIds: [...state.criteria.categoryIds],
        topicIds: [...state.criteria.topicIds],
        tags: [...state.criteria.tags],
        difficulties: [...state.criteria.difficulties],
        types: [...state.criteria.types],
      },

      // Every axis written out, with no defaults implied: a result read years from now must not
      // depend on what the defaults happened to be when it was recorded.
      policy: { ...state.policy },

      blueprint: {
        algorithm: state.blueprint.algorithm,
        seed: state.blueprint.seed,
        questionOrder: [...state.blueprint.questionOrder],
        // Derivable from the seed, so it is not duplicated here.
        optionOrder: null,
      },

      timing: {
        totalActiveMs: elapsedMs(state.clock.segments, at),
        wallClockMs: elapsedMs(state.clock.segments, at),
        timeLimitMs: state.clock.timeLimitMs,
        timeExpired: state.clock.expired,
      },

      integrity: {
        clockAnomalies: state.clock.clockAnomalies,
        visibilityLostCount: state.clock.visibilityLostCount,
      },
    },

    responses: state.blueprint.questionOrder.map((questionId, index) => {
      const entry = state.entries[questionId];
      const scored = entries[index];

      return {
        questionId,
        type: responseTypeOf(exam, questionId),
        // Present with a null response rather than omitted: otherwise "not answered" and "not
        // drawn" would look the same.
        response: entry?.response ?? null,
        score: scored?.score ?? { raw: 0, max: 0, outcome: 'unanswered' },
        ...(entry?.selfGrade !== null && entry?.selfGrade !== undefined && { selfGraded: true }),
        durationMs: scored?.durationMs ?? 0,
        visits: entry?.visits ?? 0,
        changedAnswerCount: entry?.changedAnswerCount ?? 0,
        markedForReview: entry?.markedForReview ?? false,
        ...(entry?.confidence != null && { confidence: entry.confidence }),
        // Present only when there is one, so an exam taken without notes exports exactly the file
        // it exported before this existed.
        ...(entry !== undefined && entry.note.trim() !== '' && { note: entry.note }),
        firstAnsweredAt: entry?.firstAnsweredAt ?? null,
      };
    }),

    ...(state.reports.length > 0 && {
      reports: state.reports.map((report) => ({
        questionId: report.questionId,
        reasons: [...report.reasons],
        ...(report.comment !== undefined && { comment: report.comment }),
        createdAt: report.createdAt,
      })),
    }),

    summary: {
      total: summary.total,
      answered: summary.answered,
      unanswered: summary.unanswered,
      correct: summary.correct,
      incorrect: summary.incorrect,
      partial: summary.partial,
      ungraded: summary.ungraded,
      rawScore: summary.rawScore,
      maxScore: summary.maxScore,
      scorePercent: summary.scorePercent,
      ...(summary.passed !== undefined && { passed: summary.passed }),
      durationMs: summary.durationMs,
      averageMsPerQuestion: summary.averageMsPerQuestion,
      markedForReviewCount: summary.markedForReviewCount,
      byCategory: summary.byCategory.map(cleanBreakdown),
      byDifficulty: summary.byDifficulty.map(cleanBreakdown),
      byTag: summary.byTag.map(cleanBreakdown),
      byType: summary.byType.map(cleanBreakdown),
    },
  };
}

function responseTypeOf(exam: CanonicalExam, questionId: string): string {
  const position = exam.index.byId[questionId];
  return position === undefined ? 'unknown' : (exam.questions[position]?.type ?? 'unknown');
}

function cleanBreakdown(entry: {
  id: string;
  label: string;
  total: number;
  correct: number;
  incorrect: number;
  partial: number;
  unanswered: number;
  ungraded: number;
  raw: number;
  max: number;
  percent: number;
}): Record<string, unknown> {
  return { ...entry };
}
